import { useMutation, useQuery } from '@tanstack/react-query';
import {
	completePasswordRestart,
	verifyPasswordRestartToken,
} from '@/api/query/passwordRestart';
import { checkPasswordReset } from '@/common/functions/validatePasswordReset';
import { useLanguageData } from '@/common/hooks/useLanguageData';
import { useMemo, useState } from 'react';

export const useRestartPasswordPage = (token) => {
	const t = useLanguageData('restartPassword');
	const [password, setPassword] = useState('');
	const [passwordRepeat, setPasswordRepeat] = useState('');
	const [formError, setFormError] = useState(null);
	const [isSuccess, setIsSuccess] = useState(false);

	const tokenQuery = useQuery({
		queryKey: ['password-restart-verify', token],
		queryFn: () => verifyPasswordRestartToken(token),
		enabled: Boolean(token),
		retry: false,
	});

	const completeMutation = useMutation({
		mutationFn: completePasswordRestart,
		onSuccess: () => {
			setFormError(null);
			setIsSuccess(true);
		},
		onError: (error) => {
			setIsSuccess(false);
			setFormError(
				error?.response?.data?.message
					|| error?.message
					|| t.errorGeneric,
			);
		},
	});

	const phase = useMemo(() => {
		if (!token) {
			return 'invalid';
		}

		if (tokenQuery.isLoading) {
			return 'loading';
		}

		if (tokenQuery.isError || !tokenQuery.data?.valid) {
			return 'invalid';
		}

		if (isSuccess) {
			return 'success';
		}

		return 'form';
	}, [isSuccess, token, tokenQuery.data?.valid, tokenQuery.isError, tokenQuery.isLoading]);

	const handleSubmit = (event) => {
		event.preventDefault();
		setFormError(null);

		const validation = checkPasswordReset(password, passwordRepeat);

		if (!validation.valid) {
			setFormError(t[validation.messageKey] || t.errorGeneric);
			return;
		}

		completeMutation.mutate({
			token,
			password,
			passwordRepeat,
		});
	};

	return {
		t,
		password,
		setPassword,
		passwordRepeat,
		setPasswordRepeat,
		formError,
		phase,
		isSubmitting: completeMutation.isPending,
		handleSubmit,
	};
};
